import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Camera, Loader2, Sparkles, MapPin, ChevronDown, ChevronUp, RefreshCw, Upload, AlertTriangle, Check, ArrowLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button, Input } from "../../ui";
import { BRANDS_BY_CATEGORY, PRIMARY_CATEGORIES } from "../constants";
import type { WizardData } from "../hooks/useWizardState";
import { extractLabelFromImage, getLabelGuide, lookupProductImage, findBestProductImage, type LabelLocationInstructions } from "../../../services/asset.api";
import { FindLabelPhotoSection } from "../../add-asset/steps/FindLabelPhotoSection";
import type { ApplianceIdentificationData } from "../../../services/appliance-identification.api";

// ─── Product type → wizard category/subcategory mapping ─────────────────────
const PRODUCT_TYPE_MAP: Record<string, { primaryCategory: string; subcategory: string; subcategoryLabel: string }> = {
  refrigerator: { primaryCategory: "appliances", subcategory: "refrigerator",  subcategoryLabel: "Refrigerator" },
  washer:       { primaryCategory: "appliances", subcategory: "washer",         subcategoryLabel: "Washer" },
  "washing machine": { primaryCategory: "appliances", subcategory: "washer",   subcategoryLabel: "Washer" },
  dryer:        { primaryCategory: "appliances", subcategory: "dryer",          subcategoryLabel: "Dryer" },
  dishwasher:   { primaryCategory: "appliances", subcategory: "dishwasher",     subcategoryLabel: "Dishwasher" },
  microwave:    { primaryCategory: "appliances", subcategory: "microwave",      subcategoryLabel: "Microwave" },
  oven:         { primaryCategory: "appliances", subcategory: "oven",           subcategoryLabel: "Oven" },
  range:        { primaryCategory: "appliances", subcategory: "range-stove",    subcategoryLabel: "Range / Stove" },
  stove:        { primaryCategory: "appliances", subcategory: "range-stove",    subcategoryLabel: "Range / Stove" },
  freezer:      { primaryCategory: "appliances", subcategory: "freezer",        subcategoryLabel: "Freezer" },
  "air conditioner": { primaryCategory: "home-systems", subcategory: "air-conditioner", subcategoryLabel: "Air Conditioner" },
  "air-conditioner": { primaryCategory: "home-systems", subcategory: "air-conditioner", subcategoryLabel: "Air Conditioner" },
  ac:           { primaryCategory: "home-systems", subcategory: "air-conditioner", subcategoryLabel: "Air Conditioner" },
  furnace:      { primaryCategory: "home-systems", subcategory: "furnace",      subcategoryLabel: "Furnace" },
  "heat pump":  { primaryCategory: "home-systems", subcategory: "heat-pump",    subcategoryLabel: "Heat Pump" },
  thermostat:   { primaryCategory: "home-systems", subcategory: "thermostat",   subcategoryLabel: "Thermostat" },
  "water heater": { primaryCategory: "home-systems", subcategory: "water-heater", subcategoryLabel: "Water Heater" },
  tv:           { primaryCategory: "electronics", subcategory: "tv",            subcategoryLabel: "TV" },
  television:   { primaryCategory: "electronics", subcategory: "tv",            subcategoryLabel: "TV" },
  router:       { primaryCategory: "electronics", subcategory: "router",        subcategoryLabel: "Router / Modem" },
  computer:     { primaryCategory: "electronics", subcategory: "laptop-desktop", subcategoryLabel: "Computer" },
  laptop:       { primaryCategory: "electronics", subcategory: "laptop-desktop", subcategoryLabel: "Computer" },
};

function resolveProductType(detectedType?: string): { primaryCategory: string; subcategory: string; subcategoryLabel: string } | null {
  if (!detectedType) return null;
  const key = detectedType.toLowerCase().trim();
  // Exact match first
  if (PRODUCT_TYPE_MAP[key]) return PRODUCT_TYPE_MAP[key];
  // Substring match for compound types like "Gas Oven", "Electric Dryer", "Wall Oven"
  // Check if the detected type CONTAINS any known key
  for (const [mapKey, value] of Object.entries(PRODUCT_TYPE_MAP)) {
    if (key.includes(mapKey)) return value;
  }
  return null;
}

// When no real product image can be resolved, we intentionally store NOTHING
// (empty string) rather than a generic stock (Unsplash) photo. Setting
// productImageUrl to '' unmounts the preview <img> and the wizard falls back to
// the upload prompt; downstream, web shows a category icon and the app shows a
// local bundled category image. This keeps generic images out of the database.
function getSubcategoryFallback(_subcategory?: string): string {
  return '';
}

interface Props {
  data: WizardData;
  updateData: (patch: Partial<WizardData>) => void;
  onNext: () => void;
}


export function ProductIdentification({ data, updateData, onNext }: Readonly<Props>) {
  const { t } = useTranslation('addAsset');
  const [mode, setMode] = useState<"photo" | "manual">(
    data.identificationMethod === "photo" ? "photo" : "manual"
  );
  const [dragActive, setDragActive] = useState(false);
  const [extractError, setExtractError] = useState("");
  const [modelError, setModelError] = useState("");
  const [imageLookupDone, setImageLookupDone] = useState(!!data.productImageUrl);
  const [imageNotFound, setImageNotFound] = useState(false);
  const [categoryMismatch, setCategoryMismatch] = useState<{ from: string; to: string } | null>(null);
  const [pendingCategorySwitch, setPendingCategorySwitch] = useState<{ primaryCategory: string; subcategory: string; subcategoryLabel: string } | null>(null);
  const productImageInputRef = useRef<HTMLInputElement>(null);
  const [labelInstructions, setLabelInstructions] = useState<LabelLocationInstructions | null>(null);
  const [_isGeneratingVisual] = useState(false); // kept for legacy JSX refs, always false
  const [showInstructions, setShowInstructions] = useState(false);
  const [liveStep, setLiveStep] = useState<0 | 1 | 2 | 3>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingImageSearch, setPendingImageSearch] = useState<{
    brand: string;
    model: string;
    category: string;
  } | null>(null);
  const [serpCandidates, setSerpCandidates] = useState<import('../../../services/asset.api').FindBestImageResult | null>(null);
  const [imagePickerVisible, setImagePickerVisible] = useState(false);
  const [imagePickerExiting, setImagePickerExiting] = useState(false);
  const [pickerPhase, setPickerPhase] = useState<'best' | 'alternatives'>('best');

  // Step 2: Run multi-query SERP search when extraction provides searchQueries
  useEffect(() => {
    if (!pendingImageSearch) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await findBestProductImage({
          brand: pendingImageSearch.brand,
          model: pendingImageSearch.model,
          category: pendingImageSearch.category,
        });
        if (!cancelled) {
          updateData({ isLookingUpImage: false });
          if (result.candidates?.length > 0 || result.bestImage || result.genericImage) {
            setSerpCandidates(result);
          } else {
            // No SERP results — fall back to single lookup
            await _fallbackSingleLookup();
          }
        }
      } catch {
        if (!cancelled) await _fallbackSingleLookup();
      } finally {
        if (!cancelled) setPendingImageSearch(null);
      }
    })();
    return () => { cancelled = true; };
  // _fallbackSingleLookup is intentionally excluded — it's stable per render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingImageSearch]);

  const applyCategoryFallbackImage = useCallback((subcategory?: string) => {
    const fallback = getSubcategoryFallback(subcategory || data.subcategory);
    updateData({ productImageUrl: fallback, isLookingUpImage: false });
    setImageLookupDone(true);
    setImageNotFound(false);
  }, [data.subcategory, updateData]);

  // Show picker section when SERP candidates arrive
  useEffect(() => {
    if (serpCandidates) {
      setImagePickerVisible(true);
      setImagePickerExiting(false);
      setPickerPhase('best');
    }
  }, [serpCandidates]);

  const handlePickerSelect = useCallback((confirmed: boolean, selectedImageUrl: string | null) => {
    setImagePickerExiting(true);
    setTimeout(() => {
      setImagePickerVisible(false);
      setImagePickerExiting(false);
      setSerpCandidates(null);
      if (confirmed && selectedImageUrl) {
        updateData({ productImageUrl: selectedImageUrl, isLookingUpImage: false });
        setImageLookupDone(true);
        setImageNotFound(false);
      } else {
        updateData({ productImageUrl: '', isLookingUpImage: false });
        setImageLookupDone(true);
      }
    }, 280);
  }, [updateData]);

  // Animate through pipeline steps while backend is processing
  // Step 1 = GPT-4o reading label (~0-6s), Step 2 = searching images (~6-12s), Step 3 = done
  useEffect(() => {
    if (data.isExtracting) {
      setLiveStep(1);
      const t1 = setTimeout(() => setLiveStep(2), 6000);
      const t2 = setTimeout(() => setLiveStep(3), 12000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      setLiveStep(0);
    }
  }, [data.isExtracting]);

  // DEBUG: Track extractError changes
  useEffect(() => {
    console.log('[ProductIdentification] extractError changed:', extractError);
  }, [extractError]);

  // Brands for selected category
  const suggestedBrands = BRANDS_BY_CATEGORY[data.primaryCategory] || [];

  // Image lookup is now done inside handleFile (single backend call) — no separate trigger needed.
  // This useEffect only fires for the manual-entry flow where user types brand+model.
  useEffect(() => {
    if (data.extractedData && data.brand && data.model && !imageLookupDone && !data.isLookingUpImage && !data.productImageUrl) {
      handleProductImageLookup();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.extractedData]);

  // Fetch label guide (positions + guidance + real product photo) in one call
  useEffect(() => {
    if (data.brand && data.primaryCategory && data.subcategoryLabel && !labelInstructions) {
      getLabelGuide(data.brand, data.primaryCategory, data.subcategoryLabel, data.assetType || undefined)
        .then((instructions) => {
          setLabelInstructions({
            brand: instructions.brand || data.brand,
            category: instructions.category || data.primaryCategory,
            subcategory: instructions.subcategory || data.subcategoryLabel,
            steps: instructions.steps && instructions.steps.length > 0 ? instructions.steps : [],
            alternateLocations: instructions.alternateLocations || [],
            safetyTips: instructions.safetyTips || [],
            visualGuideUrl: instructions.visualGuideUrl,
          });
        })
        .catch((err) => {
          console.error('Failed to load label guide:', err);
        });
    }
  }, [data.brand, data.primaryCategory, data.subcategoryLabel, data.assetType, labelInstructions]);

  // ── Photo Upload ──────────────────────────────────────────────────────

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) return;

      const previewUrl = URL.createObjectURL(file);
      updateData({
        labelPhotoFile: file,
        labelPhotoPreview: previewUrl,
        identificationMethod: "photo",
        isExtracting: true,
        extractedData: null, // Clear previous extraction results
        brand: "", // Clear previous data
        model: "",
        serialNumber: "",
        productImageUrl: "",
      });
      setExtractError("");
      setImageLookupDone(false);
      setImageNotFound(false);
      setCategoryMismatch(null);

      try {
        // Step 1: GPT-4o Vision — extract label fields + generate searchQueries
        const result = await extractLabelFromImage(file, data.subcategoryLabel || undefined);

        // ── Detect category mismatch — let user decide, don't auto-correct ──
        const resolved = resolveProductType(result.detectedProductType);
        let mismatchInfo: { from: string; to: string } | null = null;
        let resolvedPatch: { primaryCategory: string; subcategory: string; subcategoryLabel: string } | null = null;
        if (resolved && resolved.subcategory !== data.subcategory) {
          const fromLabel = PRIMARY_CATEGORIES
            .flatMap(c => c.subcategories)
            .find(s => s.id === data.subcategory)?.label || data.subcategoryLabel || data.subcategory;
          mismatchInfo = { from: fromLabel, to: resolved.subcategoryLabel };
          resolvedPatch = {
            primaryCategory: resolved.primaryCategory,
            subcategory: resolved.subcategory,
            subcategoryLabel: resolved.subcategoryLabel,
          };
        }

        const brandValue = result.brand || data.brand;
        const modelValue = result.model || data.model;
        const subcategoryValue = resolvedPatch?.subcategoryLabel || data.subcategoryLabel;
        const fallbackSubcategory = resolvedPatch?.subcategory || data.subcategory;

        updateData({
          extractedData: {
            brand: result.brand,
            model: result.model,
            serialNumber: result.serialNumber,
            enrichmentSteps: result.enrichmentSteps,
            dataQuality: result.dataQuality,
            enrichmentSource: result.enrichmentSource,
            detectedProductType: result.detectedProductType,
          },
          brand: brandValue,
          model: modelValue,
          serialNumber: result.serialNumber || data.serialNumber,
          isExtracting: false,
          productTitle: result.productTitle || "",
          productDescription: result.productDescription || "",
          productCategory: result.productCategory || "",
          manufacturer: result.manufacturer || "",
          mpn: result.mpn || "",
          barcode: result.barcode || "",
          productColor: result.productColor || "",
        });

        if (mismatchInfo) {
          setCategoryMismatch(mismatchInfo);
          setPendingCategorySwitch(resolvedPatch);
        }

        // Step 2: Multi-query SERP — show image confirmation dialog
        if (result.searchQueries && result.searchQueries.length > 0) {
          updateData({ isLookingUpImage: true });
          setPendingImageSearch({
            brand: brandValue,
            model: modelValue,
            category: subcategoryValue,
          });
        } else if (brandValue && modelValue) {
          await _fallbackSingleLookup();
        } else {
          applyCategoryFallbackImage(fallbackSubcategory);
        }
      } catch (err) {
        // Extract error message from backend response
        console.error('[ProductIdentification] Caught error:', err);
        console.error('[ProductIdentification] err type:', typeof err, 'instanceof Error:', err instanceof Error);

        let errorMessage = 'An error occurred while processing the image';
        try {
          errorMessage = err instanceof Error ? err.message : String(err);
          console.error('[ProductIdentification] Extracted message:', errorMessage);
        } catch (msgErr) {
          console.error('[ProductIdentification] Failed to extract message:', msgErr);
        }

        console.error('[ProductIdentification] Setting extractError to:', errorMessage);
        setExtractError(errorMessage);
        console.error('[ProductIdentification] After setExtractError');
        updateData({ isExtracting: false });
        console.error('[ProductIdentification] After updateData');
        // DON'T switch to manual mode - stay in photo mode so error is visible!
        // setMode("manual");
        console.error('[ProductIdentification] Keeping photo mode to show error');
      }
    },
    [applyCategoryFallbackImage, data.brand, data.model, data.serialNumber, data.productImageUrl, data.subcategory, data.subcategoryLabel, updateData]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => setDragActive(false);

  // ── Manual Entry ──────────────────────────────────────────────────────

  // Auto-lookup product image when brand + model are both filled
  const handleProductImageLookup = useCallback(async () => {
    if (!data.brand.trim() || !data.model.trim()) return;
    updateData({ isLookingUpImage: true });
    setImageNotFound(false);
    try {
      const imgResult = await lookupProductImage(data.brand, data.model, data.subcategoryLabel, { assetType: data.subcategoryLabel || undefined });
      if (imgResult.imageUrl) {
        updateData({ productImageUrl: imgResult.imageUrl, isLookingUpImage: false });
        setImageLookupDone(true);
        setImageNotFound(false);
      } else {
        applyCategoryFallbackImage(data.subcategory);
      }
    } catch {
      applyCategoryFallbackImage(data.subcategory);
    }
  }, [applyCategoryFallbackImage, data.brand, data.model, data.subcategory, data.subcategoryLabel, updateData]);

  const handleProductImageFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    updateData({ productImageUrl: url });
    setImageNotFound(false);
    setImageLookupDone(true);
  }, [updateData]);

  const handleCategorySwitch = () => {
    if (pendingCategorySwitch) {
      updateData(pendingCategorySwitch);
      setPendingCategorySwitch(null);
    }
    setCategoryMismatch(null);
  };

  const handleMismatchReupload = () => {
    updateData({
      labelPhotoFile: undefined,
      labelPhotoPreview: "",
      extractedData: null,
      brand: "",
      model: "",
      serialNumber: "",
      productImageUrl: "",
      isExtracting: false,
    });
    setCategoryMismatch(null);
    setPendingCategorySwitch(null);
    setExtractError("");
    setImageLookupDone(false);
    setImageNotFound(false);
  };

  const [imageFetchAttempted, setImageFetchAttempted] = useState(false);

  
  const _fallbackSingleLookup = async () => {
    updateData({ isLookingUpImage: true });
    try {
      const imgResult = await lookupProductImage(
        data.brand, data.model, data.subcategoryLabel,
        { assetType: data.subcategoryLabel || undefined }
      );
      if (imgResult.imageUrl) {
        updateData({ productImageUrl: imgResult.imageUrl, isLookingUpImage: false });
        setImageLookupDone(true);
        setImageNotFound(false);
      } else {
        applyCategoryFallbackImage(data.subcategory);
      }
    } catch {
      applyCategoryFallbackImage(data.subcategory);
    }
  };

const handleContinue = async () => {
    if (!data.brand.trim()) return;

    // Model Number is mandatory — block progression and surface an inline
    // error directly under the field (not on a later step).
    if (!data.model.trim()) {
      setModelError(t('productId.modelRequired', 'Model Number is required'));
      return;
    }

    // First click: try SERP image fetch if brand+model available and not yet tried
    if (!imageFetchAttempted && !data.productImageUrl && data.brand.trim() && data.model.trim()) {
      setImageFetchAttempted(true);
      updateData({ isLookingUpImage: true });
      try {
        const imgResult = await lookupProductImage(data.brand, data.model, data.subcategoryLabel, { assetType: data.subcategoryLabel || undefined });
        if (imgResult.imageUrl) {
          updateData({ productImageUrl: imgResult.imageUrl, isLookingUpImage: false });
          // Image found — stay on page so user can see the preview and confirm
        } else {
          // If no remote image is found, show a category image instead of blank state.
          applyCategoryFallbackImage(data.subcategory);
        }
      } catch {
        applyCategoryFallbackImage(data.subcategory);
      }
      return;
    }

    // Subsequent clicks (image already set OR user skipping image)
    if (!data.name) {
      const autoName = [data.brand, data.subcategoryLabel].filter(Boolean).join(' ');
      updateData({ name: autoName || data.subcategoryLabel });
    }
    onNext();
  };

  // Brand gates the button; Model is enforced in handleContinue so an empty
  // model surfaces an inline error under the field instead of a dead button.
  const isManualValid = data.brand.trim().length > 0;

  // Derived picker state — computed from serpCandidates. Prefer the top-N
  // candidates list (up to 3); fall back to bestImage + genericImage.
  const pickerCandidates = serpCandidates
    ? (serpCandidates.candidates && serpCandidates.candidates.length > 0
        ? serpCandidates.candidates
        : ([serpCandidates.bestImage, serpCandidates.genericImage].filter(Boolean) as any[]))
    : [];
  const pickerBest = pickerCandidates[0] ?? null;
  const pickerAlternatives = pickerCandidates.slice(1);

  // ── Removed Choice View — now start directly in manual/photo mode ─────

  // ── Photo Upload View ─────────────────────────────────────────────────

  if (mode === "photo") {
    return (
      <div className="animate-fade-in-up">
        <div className="px-6 pb-3 pt-4">
          <h2 className="text-2xl font-light tracking-tight">
            {t('productId.title', { type: data.subcategoryLabel })}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('productId.subtitle')}
          </p>
        </div>

        {/* Toggle */}
        <div className="px-6 flex gap-2 mt-2">
          <Button variant="default" size="sm" className="rounded-xl flex-1">
            {t('productId.uploadLabelPhoto')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl flex-1"
            onClick={() => {
              setMode("manual");
              updateData({ identificationMethod: "manual" });
            }}
          >
            {t('productId.enterManually')}
          </Button>
        </div>

        <div className="px-6 pb-6 mt-4">
          {/* Hidden file input — always mounted so fileInputRef is never null */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              // Reset so the same file can be re-selected
              e.target.value = '';
            }}
          />

          {/* Upload zone */}
          {data.labelPhotoPreview ? (
            <div className="space-y-3">
              {/* Image preview — compact when extracting */}
              <div className="relative rounded-2xl border-2 border-dashed border-primary/30 bg-primary/[0.02] p-3">
                <img
                  src={data.labelPhotoPreview}
                  alt={t('common.alt.productLabel')}
                  className={cn("w-full object-contain rounded-xl transition-all", data.isExtracting ? "h-20 opacity-60" : "h-28")}
                />
                {data.isExtracting && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-2 bg-background/90 border rounded-full px-3 py-1.5 shadow-sm">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                      <span className="text-xs font-medium">{t('productId.analyzing')}</span>
                    </div>
                  </div>
                )}
                {/* Re-upload button */}
                {!data.isExtracting && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute top-2 right-2 bg-background/90 hover:bg-background border rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 text-xs font-medium shadow-sm transition-colors"
                  >
                    <RefreshCw className="h-3 w-3" />
                    {t('productId.reupload')}
                  </button>
                )}
              </div>

              {/* ── Live Progress ── */}
              {data.isExtracting && liveStep > 0 && (
                <div className="rounded-xl border bg-muted/30 p-3 space-y-2.5 animate-fade-in-up">
                  {/* Step 1: AI reading label */}
                  <div className="flex items-center gap-2.5">
                    <div className={cn(
                      "w-5 h-5 rounded-full flex items-center justify-center shrink-0",
                      liveStep === 1 ? "bg-primary/10" : "bg-green-100 dark:bg-green-900/30"
                    )}>
                      {liveStep === 1
                        ? <Loader2 className="h-3 w-3 animate-spin text-primary" />
                        : <span className="text-[10px]">✅</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium leading-tight">Reading your label</p>
                      <p className="text-[10px] text-muted-foreground">
                        {liveStep === 1 ? 'GPT-4o is extracting brand, model, serial…' : 'Label details extracted'}
                      </p>
                    </div>
                  </div>

                  {/* Step 2: Finding product image */}
                  {liveStep >= 2 && (
                    <div className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Loader2 className="h-3 w-3 animate-spin text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium leading-tight">Finding product image</p>
                        <p className="text-[10px] text-muted-foreground">Searching for the best match…</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              tabIndex={0}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
              className={cn(
                "flex flex-col items-center justify-center gap-3 p-8 w-full rounded-2xl border-2 border-dashed transition-colors cursor-pointer bg-transparent",
                dragActive
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40 hover:bg-muted/50"
              )}
            >
              <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
                <Camera className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">{t('productId.clickToUploadPhoto')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('productId.uploadPhotoHelp')}
                </p>
                <p className="text-xs text-muted-foreground/60 mt-0.5">
                  {t('productId.uploadPhotoFormats')}
                </p>
              </div>
            </button>
          )}
          {extractError && (
            <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive mb-1">Unable to Process Image</p>
                <p className="text-sm text-destructive/80">{extractError}</p>
              </div>
            </div>
          )}

          {/* Extracted results */}
          {data.extractedData && !data.isExtracting && (
            <div className="mt-4 space-y-3 animate-fade-in-up">

              {/* Success badge */}
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center shrink-0">
                  <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm font-semibold text-green-700 dark:text-green-400">{t('productId.detailsExtracted')}</p>
              </div>

              {/* Category mismatch notice */}
              {categoryMismatch && (
                <div className="p-3 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-700 animate-fade-in-up">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-blue-900 dark:text-blue-200">{t('productId.aiIdentifiedDifferent')}</p>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mt-0.5">
                        <Trans
                          i18nKey="productId.looksLike"
                          ns="addAsset"
                          values={{ type: categoryMismatch.to }}
                          components={{ 1: <strong /> }}
                        />
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2.5">
                    <button
                      onClick={handleCategorySwitch}
                      className="flex-1 py-1.5 px-3 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors"
                    >
                      {t('productId.updateTo', { type: categoryMismatch.to })}
                    </button>
                    <button
                      onClick={handleMismatchReupload}
                      className="flex items-center gap-1.5 py-1.5 px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted/50 transition-colors"
                    >
                      <RefreshCw className="h-3 w-3" />
                      {t('productId.reupload')}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Inline Image Picker (2-phase) ── */}
              {imagePickerVisible && serpCandidates && (
                <div
                  className="animate-fade-in-up rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
                  style={{
                    transition: 'opacity 0.28s ease, transform 0.28s ease',
                    opacity: imagePickerExiting ? 0 : 1,
                    transform: imagePickerExiting ? 'translateY(-6px)' : 'translateY(0)',
                  }}
                >
                  {pickerPhase === 'best' ? (
                    /* ── Phase 1: Best image, prominent ── */
                    <>
                      {/* Image area */}
                      <div className="relative bg-muted/30" style={{ height: 180 }}>
                        {pickerBest ? (
                          <img
                            src={pickerBest.original}
                            alt="Product image"
                            className="w-full h-full object-contain p-5"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                            <Camera size={28} strokeWidth={1.5} />
                          </div>
                        )}
                        {pickerAlternatives.length > 0 && (
                          <span className="absolute top-2 right-2 bg-background/90 border border-border text-[10px] font-medium text-muted-foreground rounded-full px-2 py-0.5">
                            1 of {pickerCandidates.length}
                          </span>
                        )}
                      </div>

                      {/* CTA area */}
                      <div className="px-4 py-3.5 space-y-2.5">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Suggested Image</p>
                          <p className="text-[13px] font-semibold text-foreground mt-0.5 leading-snug">
                            Is this your {[data.brand, data.model].filter(Boolean).join(' ')}?
                          </p>
                        </div>

                        <Button
                          className="w-full gap-2 h-9 text-sm"
                          onClick={() => handlePickerSelect(true, pickerBest?.original ?? null)}
                        >
                          <Check size={13} />
                          Yes, use this image
                        </Button>

                        {pickerAlternatives.length > 0 ? (
                          <button
                            className="w-full text-xs text-muted-foreground hover:text-foreground py-1.5 flex items-center justify-center gap-1 transition-colors"
                            onClick={() => setPickerPhase('alternatives')}
                          >
                            No, show me other options
                            <ChevronRight size={12} className="mt-px" />
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 gap-1.5 text-xs h-8"
                              onClick={() => productImageInputRef.current?.click()}
                            >
                              <Upload size={11} /> Upload mine
                            </Button>
                            <button
                              className="text-xs text-muted-foreground hover:text-foreground px-3 transition-colors"
                              onClick={() => handlePickerSelect(false, null)}
                            >
                              Skip
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    /* ── Phase 2: Alternatives ── */
                    <>
                      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border">
                        <button
                          onClick={() => setPickerPhase('best')}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                          aria-label="Back"
                        >
                          <ArrowLeft size={14} />
                        </button>
                        <p className="text-xs font-semibold text-foreground">Choose another image</p>
                      </div>

                      <div className="p-3 space-y-2.5">
                        {pickerAlternatives.length > 0 ? (
                          <div className={`grid gap-2 ${pickerAlternatives.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {pickerAlternatives.map((c, i) => (
                              <button
                                key={c.original}
                                onClick={() => handlePickerSelect(true, c.original)}
                                className="group relative rounded-xl border-2 border-border hover:border-primary/60 overflow-hidden bg-muted/30 hover:shadow-md transition-all duration-200"
                              >
                                <div className="aspect-[4/3]">
                                  <img
                                    src={c.original}
                                    alt={`Option ${i + 2}`}
                                    className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-200"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                </div>
                                <div className="px-2 py-1.5 border-t border-border bg-card text-center">
                                  <p className="text-[10px] font-medium group-hover:text-primary transition-colors">Use this one</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-2">No other options available</p>
                        )}

                        <Button
                          variant="outline"
                          className="w-full gap-2 h-8 text-xs"
                          onClick={() => productImageInputRef.current?.click()}
                        >
                          <Upload size={12} />
                          Upload your own photo
                        </Button>

                        <button
                          onClick={() => handlePickerSelect(false, null)}
                          className="w-full text-xs text-muted-foreground hover:text-foreground py-1 transition-colors text-center"
                        >
                          Skip for now
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Product details — full-width image card + stacked fields ── */}
              {!imagePickerVisible && (
              <div className="space-y-3 animate-fade-in-up">

                {/* Full-width product image card */}
                {data.isLookingUpImage ? (
                  <div className="h-40 rounded-2xl border bg-muted/30 flex flex-col items-center justify-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Searching for product image…</p>
                  </div>
                ) : data.productImageUrl ? (
                  <div className="relative group rounded-2xl border border-border/60 bg-muted/10 overflow-hidden" style={{ height: 160 }}>
                    <img
                      src={data.productImageUrl}
                      alt={t('common.alt.product')}
                      className="w-full h-full object-contain p-4"
                      onError={(e) => {
                        const fallback = getSubcategoryFallback(data.subcategory);
                        e.currentTarget.src = fallback;
                        updateData({ productImageUrl: fallback });
                        setImageLookupDone(true);
                      }}
                    />
                    <button
                      onClick={() => { updateData({ productImageUrl: "" }); setImageLookupDone(false); productImageInputRef.current?.click(); }}
                      className="absolute top-2 right-2 flex items-center gap-1.5 bg-background/90 hover:bg-background border rounded-lg px-2.5 py-1.5 text-xs font-medium shadow-sm transition-all opacity-0 group-hover:opacity-100"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Replace
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => productImageInputRef.current?.click()}
                    className="w-full h-24 rounded-2xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-muted/30 flex flex-col items-center justify-center gap-2 transition-all bg-transparent"
                  >
                    <div className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Add product image</p>
                  </button>
                )}

                {/* Stacked fields */}
                <div className="space-y-2.5">
                  <div>
                    <label htmlFor="pi-brand" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">{t('productId.brandLabel')}</label>
                    <Input
                      id="pi-brand"
                      value={data.brand}
                      onChange={(e) => updateData({ brand: e.target.value })}
                      placeholder={t('productId.brandPlaceholder')}
                      className="rounded-xl h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="pi-model" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">{t('productId.modelLabel')} <span className="text-red-500">*</span></label>
                    <Input
                      id="pi-model"
                      value={data.model}
                      onChange={(e) => { updateData({ model: e.target.value }); if (modelError) setModelError(""); }}
                      placeholder={t('productId.modelPlaceholder')}
                      className={cn("rounded-xl h-9 text-sm", modelError && "border-red-500 focus-visible:ring-red-500")}
                      aria-invalid={!!modelError}
                    />
                    {modelError && (
                      <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        {modelError}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="pi-serial" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">
                      {t('productId.serialLabel')} <span className="normal-case font-normal opacity-60">({t('productId.serialOptional')})</span>
                    </label>
                    <Input
                      id="pi-serial"
                      value={data.serialNumber}
                      onChange={(e) => updateData({ serialNumber: e.target.value })}
                      placeholder={t('productId.serialPlaceholder', 'e.g. SN1234')}
                      className="rounded-xl h-9 text-sm"
                    />
                  </div>
                </div>

                <Button onClick={handleContinue} disabled={!isManualValid} className="w-full rounded-xl h-11">
                  {t('common.continue')}
                </Button>
              </div>
              )}

              {/* Always-mounted product image input — used both by picker and image slot */}
              <input
                id="pi-product-image"
                ref={productImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (imagePickerVisible) {
                    const url = URL.createObjectURL(f);
                    handlePickerSelect(true, url);
                  } else {
                    handleProductImageFile(f);
                  }
                  e.target.value = '';
                }}
              />
            </div>
          )}

          {/* Label finder — photo mode */}
          <div className="mt-4 overflow-hidden rounded-2xl border border-primary/20" style={{background: 'linear-gradient(135deg, hsl(var(--primary)/0.06) 0%, white 60%, hsl(var(--primary)/0.04) 100%)'}}>
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <MapPin className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground flex items-center gap-2 flex-wrap">
                    {t('productId.toLocateYourLabel')}
                    <span className="text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <Sparkles className="h-2.5 w-2.5" />
                      {t('productId.aiPowered')}
                    </span>
                  </p>
                  {!showInstructions && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('productId.getAiGuidance')}
                    </p>
                  )}
                </div>
              </div>
              {showInstructions ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </button>

            {showInstructions && (
              <div className="px-4 pb-4 border-t border-primary/10 pt-3 space-y-4 animate-fade-in-up">
                <FindLabelPhotoSection
                  assetTypeHint={data.subcategoryLabel || data.subcategory}
                  onIdentified={(identified: ApplianceIdentificationData) => {
                    if (identified.brand && !data.brand) updateData({ brand: identified.brand });
                    if (identified.assetType) {
                      const matched = resolveProductType(identified.assetType);
                      if (matched) updateData({ primaryCategory: matched.primaryCategory, subcategory: matched.subcategory, subcategoryLabel: matched.subcategoryLabel });
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Manual Entry View ─────────────────────────────────────────────────

  let continueLabel = 'Continue';
  if (data.isLookingUpImage) continueLabel = 'Finding image...';
  else if (imageNotFound && !data.productImageUrl) continueLabel = 'Continue without image';

  return (
    <div className="animate-fade-in-up">
      <div className="px-6 pb-3 pt-4">
        <h2 className="text-2xl font-light tracking-tight">
          {t('productId.title', { type: data.subcategoryLabel })}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('productId.subtitle')}
        </p>
      </div>

      {/* Toggle — same as photo mode */}
      <div className="px-6 flex gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl flex-1"
          onClick={() => {
            setMode("photo");
            updateData({ identificationMethod: "photo" });
          }}
        >
          {t('productId.uploadLabelPhoto')}
        </Button>
        <Button variant="default" size="sm" className="rounded-xl flex-1">
          {t('productId.enterManually')}
        </Button>
      </div>

      <div className="px-6 pb-6 mt-4 space-y-4">
        {/* Brand */}
        <div>
          <label htmlFor="manual-brand" className="text-sm font-medium mb-1.5 block">{t('productId.brandLabel')} *</label>
          <Input
            id="manual-brand"
            value={data.brand}
            onChange={(e) => updateData({ brand: e.target.value })}
            placeholder={t('productId.brandPlaceholder')}
            className="rounded-xl"
          />
          {/* Quick-pick brand buttons */}
          {suggestedBrands.length > 0 && !data.brand && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {suggestedBrands.slice(0, 8).map((b) => (
                <button
                  key={b}
                  onClick={() => updateData({ brand: b })}
                  className="text-xs px-2.5 py-1 rounded-lg border bg-background hover:bg-primary/5 hover:border-primary/30 transition-colors"
                >
                  {b}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Model + Serial */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="manual-model" className="text-sm font-medium mb-1.5 block">{t('productId.modelLabel')} <span className="text-red-500">*</span></label>
            <Input
              id="manual-model"
              value={data.model}
              onChange={(e) => { updateData({ model: e.target.value }); if (modelError) setModelError(""); }}
              placeholder={t('productId.modelPlaceholder')}
              className={cn("rounded-xl", modelError && "border-red-500 focus-visible:ring-red-500")}
              aria-invalid={!!modelError}
            />
            {modelError && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {modelError}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="manual-serial" className="text-sm font-medium mb-1.5 block">
              {t('productId.serialLabel')} <span className="text-muted-foreground text-xs">{t('productId.serialOptional')}</span>
            </label>
            <Input
              id="manual-serial"
              value={data.serialNumber}
              onChange={(e) => updateData({ serialNumber: e.target.value })}
              placeholder={t('productId.serialPlaceholder')}
              className="rounded-xl"
            />
          </div>
        </div>

        {/* ── Product Image Section ───────────────────────────────── */}

        {/* Loading state */}
        {data.isLookingUpImage && (
          <div className="flex items-center gap-3 p-4 rounded-xl border bg-primary/5 border-primary/20">
            <Loader2 className="h-5 w-5 animate-spin shrink-0 text-primary" />
            <div>
              <p className="text-sm font-semibold">{t('productId.searchingProductImage')}</p>
              <p className="text-xs text-muted-foreground">{t('identify.searchingFor', { brand: data.brand, model: data.model })}</p>
            </div>
          </div>
        )}

        {/* Image found */}
        {data.productImageUrl && !data.isLookingUpImage && (
          <div className="flex items-center gap-3 p-3 rounded-xl border bg-green-50 border-green-200">
            <img
              src={data.productImageUrl}
              alt={t('common.alt.product')}
              className="w-16 h-16 object-contain rounded-lg bg-white border border-gray-100 shrink-0"
              onError={(e) => {
                const fallback = getSubcategoryFallback(data.subcategory);
                e.currentTarget.src = fallback;
                updateData({ productImageUrl: fallback });
              }}
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-800">{t('productId.productImageFound')}</p>
              <p className="text-xs text-green-600">{data.brand} {data.model}</p>
            </div>
            <button
              onClick={() => { updateData({ productImageUrl: '' }); setImageNotFound(false); setImageFetchAttempted(false); }}
              className="text-xs text-muted-foreground hover:text-foreground p-1"
              title={t('productId.removeImage')}
            >
              ✕
            </button>
          </div>
        )}

        {/* Image not found — manual upload option */}
        {imageNotFound && !data.productImageUrl && !data.isLookingUpImage && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                <Trans
                  i18nKey="productId.noImageFoundForBrand"
                  ns="addAsset"
                  values={{ brand: data.brand, model: data.model }}
                  components={{ 1: <strong /> }}
                />
              </p>
            </div>
            <button
              type="button"
              tabIndex={0}
              onClick={() => productImageInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') productImageInputRef.current?.click(); }}
              className="flex flex-col items-center gap-2 p-4 w-full rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-muted/30 cursor-pointer transition-colors bg-transparent"
            >
              <Upload className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium">{t('productId.uploadProductImageManually')}</p>
              <p className="text-xs text-muted-foreground">{t('productId.imageFormatsHint')}</p>
            </button>
            <input
              ref={productImageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleProductImageFile(f); }}
            />
          </div>
        )}

        <Button
          onClick={handleContinue}
          disabled={!isManualValid || data.isLookingUpImage}
          className="w-full rounded-xl mt-2"
        >
          {continueLabel}
        </Button>
        {/* Label finder — manual mode */}
        <div className="overflow-hidden rounded-2xl border border-primary/20" style={{background: 'linear-gradient(135deg, hsl(var(--primary)/0.06) 0%, white 60%, hsl(var(--primary)/0.04) 100%)'}}>
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-primary/5 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground flex items-center gap-2 flex-wrap">
                  {t('productId.toLocateYourLabel')}
                  <span className="text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                    <Sparkles className="h-2.5 w-2.5" />
                    {t('productId.aiPowered')}
                  </span>
                </p>
                {!showInstructions && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('productId.getAiGuidance')}
                  </p>
                )}
              </div>
            </div>
            {showInstructions ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
          </button>

          {showInstructions && (
            <div className="px-4 pb-4 border-t border-primary/10 pt-3 space-y-4 animate-fade-in-up">
              <FindLabelPhotoSection
                assetTypeHint={data.subcategoryLabel || data.subcategory}
                onIdentified={(identified: ApplianceIdentificationData) => {
                  if (identified.brand && !data.brand) updateData({ brand: identified.brand });
                  if (identified.assetType) {
                    const matched = resolveProductType(identified.assetType);
                    if (matched) updateData({ primaryCategory: matched.primaryCategory, subcategory: matched.subcategory, subcategoryLabel: matched.subcategoryLabel });
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
